import { Badge, Box, Button, Flex, FormControl, FormLabel, Grid, GridItem, IconButton, Select, Spinner, Text, Textarea, Tooltip, Checkbox, Radio, RadioGroup, Stack } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { CustomTable, CustomToast, Dropdown, FormInput } from "../../../../components";
import { Column } from "../../../../components/customTable/CustomTable";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useFetch } from "../../../../hooks/useFetch";
import { showDate, showDateTime, showTime } from "../../../../utilities/helper";
import * as yup from "yup";
import { EditIcon } from "@chakra-ui/icons";
import { MultiSelectMenu } from "../../../../components/MultiSelectMenu/MultiSelectMenu";
import { refreshHistory } from "../../../../components/History/modal";
import MyDiv from "./followUpFormTabV2.style";

interface Props {
    leadId: number;
    viewType?: "table" | "card";
    onFollowUpSaved?: (leadId: number) => void;
}
interface IFollowUp {
    id: number;
    leadId: number;
    nextFollowupDate: string;
    followTime: string;
    description: string;
    createdAt: string;
    createdBy: string;
    createdByName?: string;
    remarks?: string;
    isCompleted: boolean;
    followUp_Status?: string;
    messageTypes: { id: number; name: string }[];
    messageTypeName: string;
}
interface IFollowUpForm {
    nextFollowupDate: string;
    followTime: string;
    description: string;
    remarks?: string;
    status: string;
}

const defaultValues: IFollowUpForm = {
    nextFollowupDate: "",
    followTime: "",
    description: "",
    remarks: "",
    status: "",
};

export enum MessageType {
    Email = 1,
    SMS = 2,
    Whatsapp = 3,
    Call = 4,
}

const messageTypeOptions = [
    { id: MessageType.Email, name: "Email" },
    { id: MessageType.SMS, name: "SMS" },
    { id: MessageType.Whatsapp, name: "Whatsapp" },
    { id: MessageType.Call, name: "Call" },
];
const schema = yup.object({
    nextFollowupDate: yup.string().required("Reminder date is required"),
    followTime: yup.string().required("Reminder time is required"),
    remarks: yup.string().optional(),
    description: yup.string().optional()

});

const FollowUpFormTabV2 = ({ leadId, onFollowUpSaved }: Props) => {
    const [showForm, setShowForm] = useState(true);
    const [followUps, setFollowUps] = useState<IFollowUp[]>([]);
    const { addToast } = CustomToast();
    const { fetchApi, loading } = useFetch(addToast);
    const [editId, setEditId] = useState<number | null>(null);
    const [selectedMessageTypes, setSelectedMessageTypes] = useState<number[]>([]);
    const [followUpFilter, setFollowUpFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [filterLoading, setFilterLoading] = useState(false);
    const today = new Date().toISOString().split("T")[0];
    const [showRemarks, setShowRemarks] = useState(false);
    const [isCancelledRecord, setIsCancelledRecord] = useState(false);

    const [includeSignature, setIncludeSignature] = useState(true);
    const [signatureType, setSignatureType] = useState<"Short" | "Full">("Full");
    const [includeDisclaimer, setIncludeDisclaimer] = useState(true);

    const [userSignature, setUserSignature] = useState<{ shortSignature: string; fullSignature: string } | null>(null);
    const [websiteDisclaimer, setWebsiteDisclaimer] = useState<string>("");
    const [loadingSignature, setLoadingSignature] = useState(false);
    const [loadingDisclaimer, setLoadingDisclaimer] = useState(false);

    useEffect(() => {
        const fetchSignature = async () => {
            if (includeSignature && !userSignature && !loadingSignature) {
                setLoadingSignature(true);
                try {
                    const res = await fetchApi<{ shortSignature: string; fullSignature: string }>("UserSignature", "GET");
                    if (res) {
                        setUserSignature({
                            shortSignature: res.shortSignature || "",
                            fullSignature: res.fullSignature || "",
                        });
                    }
                } catch (error) {
                    console.error("Error fetching signature:", error);
                } finally {
                    setLoadingSignature(false);
                }
            }
        };
        fetchSignature();
    }, [includeSignature, userSignature, loadingSignature]);

    useEffect(() => {
        const fetchDisclaimer = async () => {
            if (includeDisclaimer && !websiteDisclaimer && !loadingDisclaimer) {
                setLoadingDisclaimer(true);
                try {
                    const res = await fetchApi<{ disclaimer: string }>("WebsiteSetting", "GET");
                    if (res) {
                        setWebsiteDisclaimer(res.disclaimer || "");
                    }
                } catch (error) {
                    console.error("Error fetching disclaimer:", error);
                } finally {
                    setLoadingDisclaimer(false);
                }
            }
        };
        fetchDisclaimer();
    }, [includeDisclaimer, websiteDisclaimer, loadingDisclaimer]);

    const { control, handleSubmit, reset, register, formState: { errors },
    } = useForm<IFollowUpForm>({
        resolver: yupResolver(schema) as any,
        defaultValues,
    });

    const meetingFilters = [
        { id: "upcoming", name: "Upcoming" },
        { id: "all", name: "All" },
        { id: "previous", name: "Previous" }
    ];

    const statusFilters = [
        { id: "all", name: "All" },
        { id: "pending", name: "Pending" },
        { id: "cancelled", name: "Cancelled" },
        { id: "done", name: "Done" },
    ];

    const loadFollowUps = async () => {
        setFilterLoading(true);
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const res = await fetchApi(`LeadRequirement/LeadFollowUp?leadId=${leadId}&filter=${followUpFilter}&status=${statusFilter}&timeZone=${encodeURIComponent(timezone)}`, "GET");
        if (Array.isArray(res?.data)) {
            setFilterLoading(false);
            setFollowUps(res.data);
        } else {
            setFollowUps([]);
        }
    };

    useEffect(() => {
        if (leadId) {
            loadFollowUps();
        }
    }, [leadId, followUpFilter, statusFilter]);

    const onSubmit = async (data: IFollowUpForm) => {

        if (selectedMessageTypes.length === 0) {
            addToast({
                message: "Please select at least one message type", status: "warning",
            });
            return;
        }

        if (!editId) {
            const selectedDateTime = new Date(
                `${data.nextFollowupDate}T${data.followTime}:00`
            );
            if (selectedDateTime <= new Date()) {
                addToast({
                    message: "Reminder time must be greater than current time.",
                    status: "warning",
                });
                return;
            }
        }
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const followTimeUtc = data.followTime && data.nextFollowupDate ? new Date(`${data.nextFollowupDate}T${data.followTime}:00`).toISOString() : null;
        const payload = {
            leadId,
            nextFollowupDate: data.nextFollowupDate,
            followTime: followTimeUtc,
            description: data.description,
            messageType: selectedMessageTypes,
            isCompleted: editId ? showRemarks : false,
            followUp_Status: data.status,
            remarks: data.remarks,
            timezone: timezone,
            IncludeSignature: includeSignature,
            IncludeDisclaimer: includeDisclaimer,
            SignatureType: signatureType,
        };

        const res = editId ? await fetchApi(`LeadRequirement/LeadFollowUp/${editId}`, "PUT", payload, null, "Reminder Updated Successfully")
            : await fetchApi("LeadRequirement/LeadFollowUp", "POST", payload, null, "Reminder Added Successfully");
        if (res) {
            setEditId(null);
            setIncludeSignature(false);
            setIncludeDisclaimer(false);
            setSignatureType("Short");
            reset(defaultValues);
            loadFollowUps();
            onFollowUpSaved?.(leadId);
            refreshHistory("Lead", leadId);
        }
    };

    const stripHtml = (html: string) => {
        const doc = new DOMParser().parseFromString(html || "", "text/html");
        return doc.body.textContent || "";
    };

    const closeModal = () => {
        setEditId(null);
        setShowRemarks(false);
        setSelectedMessageTypes([]);
        setIncludeSignature(false);
        setIncludeDisclaimer(false);
        setSignatureType("Short");
        reset(defaultValues);
    };

    const isPastFollowUp = (row: IFollowUp) => {
        if (!row.followTime) return false;
        const followUpDateTime = new Date(`${row.followTime}Z`);
        return followUpDateTime.getTime() < Date.now();
    };

    const handleEdit = (row: IFollowUp) => {
        setEditId(row.id);
        setIsCancelledRecord(row.followUp_Status === "Cancelled");
        reset({
            nextFollowupDate: row.nextFollowupDate?.split("T")[0],
            followTime: row.followTime ? new Date(row.followTime.endsWith("Z") ? row.followTime : `${row.followTime}Z`).toLocaleTimeString("en-GB", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            }) : "",
            description: row.description,
            remarks: row.remarks || "",
            status: row.followUp_Status || "",
        });
        setSelectedMessageTypes(
            row.messageTypes?.map((x) => x.id) || []
        );
        setShowRemarks(row.isCompleted);
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const getStatusBadge = (status?: string, isPast?: boolean) => {
        const label = status || "Pending";
        if (label === "Cancelled") {
            return <Badge px={2.5} py={0.5} borderRadius="full" bg="#FEF3F2" color="#B42318" border="1px solid #FECDCA" textTransform="none" fontSize="xs" fontWeight="500">{label}</Badge>;
        }
        if (label === "Done" || label === "Completed") {
            return <Badge px={2.5} py={0.5} borderRadius="full" bg="#ECFDF3" color="#027A48" border="1px solid #ABEFC6" textTransform="none" fontSize="xs" fontWeight="500">{label}</Badge>;
        }
        if (isPast) {
            return <Badge px={2.5} py={0.5} borderRadius="full" bg="#FFFAEB" color="#B54708" border="1px solid #FEDF89" textTransform="none" fontSize="xs" fontWeight="500">Overdue</Badge>;
        }
        return <Badge px={2.5} py={0.5} borderRadius="full" bg="#EFF8FF" color="#175CD3" border="1px solid #B2DDFF" textTransform="none" fontSize="xs" fontWeight="500">{label}</Badge>;
    };

    const parseFollowUpDateTime = (followTime: string) => {
        return new Date(followTime.endsWith("Z") ? followTime : `${followTime}Z`);
    };



    const isFollowUpApproaching = (row: IFollowUp) => {
        if (
            row.followUp_Status === "Cancelled" ||
            row.followUp_Status === "Done" ||
            row.isCompleted ||
            !row.followTime
        ) {
            return false;
        }

        const now = new Date();
        const followUpTime = parseFollowUpDateTime(row.followTime);

        const diffMinutes =
            (followUpTime.getTime() - now.getTime()) / (1000 * 60);

        return diffMinutes <= 15 && diffMinutes >= 0;
    };

    const isFollowUpToday = (row: IFollowUp) => {
        if (
            row.followUp_Status === "Cancelled" ||
            row.followUp_Status === "Done" ||
            row.isCompleted ||
            !row.followTime ||
            isPastFollowUp(row) ||
            isFollowUpApproaching(row)
        ) {
            return false;
        }

        const today = new Date();
        const followUpTime = parseFollowUpDateTime(row.followTime);

        return (
            followUpTime.getFullYear() === today.getFullYear() &&
            followUpTime.getMonth() === today.getMonth() &&
            followUpTime.getDate() === today.getDate()
        );
    };

    return (
        <MyDiv>
            <Box>
                <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={3}>
                    <Text className="font-poppins text_lg text_semibold">
                        Reminders
                    </Text>

                    <Flex gap={3} align="center" wrap="wrap">
                        <Select size="sm" w="120px" bg="white" borderRadius="8px" borderColor="#D0D5DD" value={statusFilter} onChange={(e: any) => setStatusFilter(e.target.value)}>
                            {statusFilters.map((item) => (
                                <option key={item.id} value={item.id}>
                                    {item.name}
                                </option>
                            ))}
                        </Select>

                        <Select size="sm" w="120px" bg="white" borderRadius="8px" borderColor="#D0D5DD" value={followUpFilter} onChange={(e: any) => setFollowUpFilter(e.target.value)}>
                            {meetingFilters.map((item) => (
                                <option key={item.id} value={item.id}>
                                    {item.name}
                                </option>
                            ))}
                        </Select>
                    </Flex>
                </Flex>

                {showForm && (
                    <Box
                        mb={6}
                        p={5}
                        bg="white"
                        border="1px solid #EAECF0"
                        borderRadius="12px"
                        boxShadow="0px 1px 2px rgba(16, 24, 40, 0.05)"
                    >
                        <Flex justify="space-between" align="center" mb={4} pb={3} borderBottom="1px solid #F2F4F7">
                            <Text fontSize="md" fontWeight="600" color="#101828">
                                {editId ? "Edit Reminder Details" : "Schedule a New Reminder"}
                            </Text>
                        </Flex>

                        <form noValidate onSubmit={handleSubmit(onSubmit)}>
                            <Grid templateColumns="repeat(3,1fr)" gap={3}>
                                {/* Row 1: Date, Time, Channel */}
                                <GridItem colSpan={1}>
                                    <FormInput isRequired name="nextFollowupDate" control={control} label="Reminder Date" type="date" errors={errors} min={today} disable={isCancelledRecord} />
                                </GridItem>

                                <GridItem colSpan={1}>
                                    <FormInput isRequired name="followTime" control={control} label="Reminder Time" type="time" errors={errors} disable={isCancelledRecord} />
                                </GridItem>

                                <GridItem colSpan={1}>
                                    <FormControl isRequired mt={2} isDisabled={isCancelledRecord}>
                                        <FormLabel className='font-poppins text_medium text_md ' color="#344054" mb={2}>
                                            Reminder Channel
                                        </FormLabel>
                                        <MultiSelectMenu options={messageTypeOptions} labelKey="name" valueKey="id" selectedValues={selectedMessageTypes} setSelectedValues={setSelectedMessageTypes} placeholder="Select Message Types" isDisabled={isCancelledRecord} />
                                    </FormControl>
                                </GridItem>

                                {/* Row 2: Status & Remarks (Only on Edit) */}
                                {editId && (
                                    <GridItem colSpan={1}>
                                        <FormControl mt={2} isDisabled={isCancelledRecord}>
                                            <FormLabel className="font-poppins text_medium text_md" color="#344054" mb={2}>
                                                Status
                                            </FormLabel>
                                            <Select {...register("status")} bg="white" borderColor="#D0D5DD" borderRadius="8px" size="md">
                                                <option value="">Select Status</option>
                                                <option value="Done">Done</option>
                                                <option value="Pending">Pending</option>
                                                <option value="Cancelled">Cancelled</option>
                                            </Select>
                                        </FormControl>
                                    </GridItem>
                                )}

                                {editId && (
                                    <GridItem colSpan={2}>
                                        <FormControl mt={2} isDisabled={isCancelledRecord}>
                                            <FormLabel className='font-poppins text_medium text_md ' color="#344054" mb={2}>Remarks</FormLabel>
                                            <Textarea placeholder="Add Remarks" bg="white" borderColor="#D0D5DD" borderRadius="8px" minH="50px" height="60px" className="font_poppins text_regular text_sm" {...register(`remarks`)} />
                                        </FormControl>
                                    </GridItem>
                                )}

                                {/* Row 3: Description */}
                                <GridItem colSpan={3}>
                                    <FormControl mt={2} isDisabled={isCancelledRecord}>
                                        <FormLabel className='font-poppins text_medium text_md ' color="#344054" mb={2}>Description</FormLabel>
                                        <Textarea placeholder="Add Description" bg="white" borderColor="#D0D5DD" borderRadius="8px" minH="50px" height="60px" className="font_poppins text_regular text_sm" {...register(`description`)} />
                                        {/* {errors.description && (
                                        <Text color="red.500" fontSize="sm" mt={1}>
                                            {errors.description.message}
                                        </Text>
                                    )} */}
                                    </FormControl>
                                </GridItem>

                                {/* <GridItem colSpan={3}>
                                    <FormControl>
                                        <FormLabel>Email Options</FormLabel>

                                        <Flex direction="column" gap={4}>
                                            <Box>
                                                <Checkbox isChecked={includeSignature}
                                                    onChange={(e) => setIncludeSignature(e.target.checked)}
                                                    isDisabled={isCancelledRecord}
                                                >
                                                    Show Signature
                                                </Checkbox>
                                                {includeSignature && (
                                                    <Box mt={3} pl={6} borderLeft="2px solid" borderColor="blue.100">
                                                        <RadioGroup onChange={(val) => setSignatureType(val as "Short" | "Full")} value={signatureType}>
                                                            <Flex gap={4} mb={2}>
                                                                <Radio value="Short" colorScheme="blue" isDisabled={isCancelledRecord}>Short Signature</Radio>
                                                                <Radio value="Full" colorScheme="blue" isDisabled={isCancelledRecord}>Full Signature</Radio>
                                                            </Flex>
                                                        </RadioGroup>
                                                        {loadingSignature ? (
                                                            <Flex align="center" gap={2} mt={2}>
                                                                <Spinner size="xs" color="#4048D6" />
                                                                <Text fontSize="xs" color="gray.500">Loading signature...</Text>
                                                            </Flex>
                                                        ) : (
                                                            <Box 
                                                                mt={2} 
                                                                p={3} 
                                                                bg="gray.50" 
                                                                borderRadius="md" 
                                                                border="1px solid" 
                                                                borderColor="gray.200" 
                                                                maxH="150px" 
                                                                overflowY="auto"
                                                                fontSize="sm"
                                                            >
                                                                {signatureType === "Short" ? (
                                                                    userSignature?.shortSignature ? (
                                                                        <Box dangerouslySetInnerHTML={{ __html: userSignature.shortSignature }} />
                                                                    ) : (
                                                                        <Text color="gray.400" fontStyle="italic">No short signature configured.</Text>
                                                                    )
                                                                ) : (
                                                                    userSignature?.fullSignature ? (
                                                                        <Box dangerouslySetInnerHTML={{ __html: userSignature.fullSignature }} />
                                                                    ) : (
                                                                        <Text color="gray.400" fontStyle="italic">No full signature configured.</Text>
                                                                    )
                                                                )}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>

                                            <Box>
                                                <Checkbox
                                                    isChecked={includeDisclaimer}
                                                    onChange={(e) => setIncludeDisclaimer(e.target.checked)}
                                                    isDisabled={isCancelledRecord}
                                                >
                                                    Show Disclaimer
                                                </Checkbox>
                                                {includeDisclaimer && (
                                                    <Box mt={3} pl={6} borderLeft="2px solid" borderColor="blue.100">
                                                        {loadingDisclaimer ? (
                                                            <Flex align="center" gap={2}>
                                                                <Spinner size="xs" color="#4048D6" />
                                                                <Text fontSize="xs" color="gray.500">Loading disclaimer...</Text>
                                                            </Flex>
                                                        ) : (
                                                            <Box 
                                                                p={3} 
                                                                bg="gray.50" 
                                                                borderRadius="md" 
                                                                border="1px solid" 
                                                                borderColor="gray.200" 
                                                                maxH="150px" 
                                                                overflowY="auto"
                                                                fontSize="sm"
                                                            >
                                                                {websiteDisclaimer ? (
                                                                    <Box dangerouslySetInnerHTML={{ __html: websiteDisclaimer }} />
                                                                ) : (
                                                                    <Text color="gray.400" fontStyle="italic">No disclaimer configured.</Text>
                                                                )}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Flex>
                                    </FormControl>
                                </GridItem> */}
                            </Grid>

                            <Flex justify="flex-end" mt={6} pt={4} borderTop="1px solid #F2F4F7" gap={3}>
                                {editId && (
                                    <Button variant="outline" size="sm" h="36px" borderRadius="8px" borderColor="#D0D5DD" color="#344054" _hover={{ bg: "#F9FAFB" }} onClick={closeModal}>
                                        Cancel
                                    </Button>
                                )}
                                <Button bg="#4048D6" color="white" size="sm" h="36px" borderRadius="8px" _hover={{ bg: "#353CC4" }} type="submit" isLoading={loading} isDisabled={isCancelledRecord}>
                                    {editId ? "Update" : "Save"}
                                </Button>
                            </Flex>
                        </form>
                    </Box>
                )}

                {/* Cards List */}
                <Box>
                    {filterLoading ? (
                        <Flex justify="center" align="center" height="300px">
                            <Spinner thickness="2px" speed="0.65s" color="#0052CC" size="lg" />
                        </Flex>
                    ) : followUps.length === 0 ? (
                        <Flex direction="column" justify="center" align="center" height="300px">
                            <Text color="gray.500">No Reminders Found</Text>
                        </Flex>
                    ) : (
                        <Grid gap={4}>
                            {followUps.map((followUp) => {
                                const isOverdue = isPastFollowUp(followUp);
                                return (
                                    <Box
                                        key={followUp.id}
                                        bg="white"
                                        border="1px solid #EAECF0"
                                        borderRadius="12px"
                                        p={5}
                                        shadow="sm"
                                        transition="all 0.2s"
                                        _hover={{ shadow: "md", borderColor: "#D0D5DD" }}
                                    >
                                        <Flex justify="space-between" align="start" mb={4}>
                                            <Box>
                                                <Flex align="center" gap={3} mb={1}>
                                                    <Text fontSize="lg" fontWeight="600" color="#101828">
                                                        {followUp.messageTypeName}
                                                    </Text>
                                                    {getStatusBadge(followUp.followUp_Status, isOverdue)}
                                                </Flex>
                                                <Text fontSize="sm" color="gray.500">
                                                    Created by {followUp.createdByName || followUp.createdBy}
                                                </Text>
                                            </Box>
                                            <IconButton
                                                aria-label="Edit Reminder"
                                                icon={<EditIcon />}
                                                size="sm"
                                                variant="ghost"
                                                color="#667085"
                                                _hover={{ color: "#4048D6", bg: "#F9FAFB" }}
                                                onClick={() => handleEdit(followUp)}
                                            />
                                        </Flex>

                                        <Grid
                                            templateColumns={{
                                                base: "repeat(2,1fr)",
                                                md: "repeat(3,1fr)",
                                            }}
                                            gap={4}
                                            p={3.5}
                                            borderRadius="8px"
                                            border="1px solid"
                                            bg={
                                                followUp.followUp_Status === "Cancelled" || isPastFollowUp(followUp)
                                                    ? "#FEE2E2"
                                                    : "#F9FAFB"
                                            }
                                            borderColor={
                                                followUp.followUp_Status === "Cancelled" || isPastFollowUp(followUp)
                                                    ? "#FCA5A5"
                                                    : "#F2F4F7"
                                            }
                                            className={
                                                followUp.followUp_Status === "Cancelled" || isPastFollowUp(followUp)
                                                    ? "expired-row"
                                                    : isFollowUpApproaching(followUp)
                                                        ? "blink-approaching"
                                                        : isFollowUpToday(followUp)
                                                            ? "blink-today"
                                                            : ""
                                            }
                                        >
                                            <Box>
                                                <Text color="#667085" fontSize="12px" fontWeight="500" mb={0.5}>Reminder Date</Text>
                                                <Text fontWeight="600" color="#344054" fontSize="sm">{showDate(followUp.followTime)}</Text>
                                            </Box>
                                            <Box>
                                                <Text color="#667085" fontSize="12px" fontWeight="500" mb={0.5}>Time</Text>
                                                <Text fontWeight="600" color="#344054" fontSize="sm">{showTime(followUp.followTime)}</Text>
                                            </Box>
                                            <Box>
                                                <Text color="#667085" fontSize="12px" fontWeight="500" mb={0.5}>Created At</Text>
                                                <Text fontWeight="600" color="#344054" fontSize="sm">{showDateTime(followUp.createdAt)}</Text>
                                            </Box>
                                        </Grid>

                                        {followUp.description && (
                                            <Box mt={4} pt={3.5} borderTop="1px solid #EAECF0">
                                                <Text color="#344054" fontSize="13px" fontWeight="500" mb={1}>Description</Text>
                                                <Text fontSize="13px" color="#475467" style={{ whiteSpace: "pre-line" }}>{stripHtml(followUp.description)}</Text>
                                            </Box>
                                        )}

                                        {followUp.remarks && (
                                            <Box mt={3} pt={3} borderTop="1px dashed #EAECF0">
                                                <Text color="#344054" fontSize="13px" fontWeight="500" mb={1}>Remarks</Text>
                                                <Text fontSize="13px" color="#475467" style={{ whiteSpace: "pre-line" }}>{followUp.remarks}</Text>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Grid>
                    )}
                </Box>
            </Box>
        </MyDiv>
    );
};

export default FollowUpFormTabV2;
